// Focused sub-agent that exists for exactly one job: given a
// natural-language description of an analytics query the parent
// can't get right, iterate against the schema and return a
// VALIDATED Query spec. The parent then runs `analytics_query`
// itself with that spec, so the resulting rows land in the parent's
// own context + toolResultStore — no cross-agent context plumbing.
//
// Why this exists as its own tool instead of an `spawn_subagent`
// caller-side recipe:
//   • Pi-agent-core has a soft retry cap that nudges the parent to
//     hand off after a couple of failed analytics_query attempts. By
//     the time that fires, the parent has already burnt cycles on
//     the same broken spec. A dedicated tool the parent can reach
//     for BEFORE looping changes the failure shape — "I tried 1-2
//     things, then asked the specialist" instead of "I burned 3
//     turns retrying then gave up and delegated."
//   • The contract is narrow: input is a task description, output
//     is a spec. No structured-output schema gymnastics in the
//     parent's tool call. The parent just reads `{ spec: {...} }`.
//   • The specialist gets the same schema-discovery toolkit the
//     parent has, plus analytics_query for dry-run validation —
//     but unbounded iteration; no parent-side retry cap leaking
//     in.

import { mirrorTranscript, refreshMirror } from "./agentTranscript.js";
import { Type } from "@earendil-works/pi-ai";
import { runSubAgent } from "./subAgent.js";
import { currentDateSection } from "./promptDate.js";

const SPECIALIST_TOOLKIT = [
    "schema_list_entities",
    "schema_describe_entity",
    "schema_search",
    "analytics_query",
];

const OUTPUT_SCHEMA = {
    type: "object",
    description: "A validated analytics_query spec ready to run.",
    required: ["spec"],
    properties: {
        spec: {
            type: "object",
            description: "The Query spec (root / filter / output / etc. — same shape analytics_query.parameters.spec accepts).",
        },
        notes: {
            type: "string",
            description: "Optional one-line note explaining what made this spec different from the parent's failed attempts (which field name was wrong, which traversal was missing, etc.). Helps the parent learn for next time.",
        },
    },
};

function buildSpecialistSystemPrompt() {
    return [
        "You are a focused query-construction specialist. Your only job: produce a validated analytics_query spec for the task the parent agent describes.",
        "",
        "Workflow:",
        "  1. Read the parent's task and any failed_attempt they supply.",
        "  2. Use `schema_list_entities` / `schema_describe_entity` / `schema_search` to confirm the entities, fields, traversals, and operator vocabulary you need. Don't assume — the parent likely got something wrong about field names or edge directions.",
        "  3. Compose a candidate spec. Run it via `analytics_query` to dry-run. If it errors, read the error, look up the right names, fix, retry. There's no retry cap here — iterate until it runs cleanly.",
        "  4. Once the spec returns rows successfully, call `return_result` with `{ spec, notes }` where `spec` is exactly the JSON you handed to analytics_query.",
        "",
        "Rules:",
        "  • Return ONLY the spec, not the rows. The parent will run analytics_query itself with your spec; they want the recipe, not your sample data.",
        "  • Do NOT call `render_block` or any other tool besides the four in your toolkit. You're not finishing an analysis, you're building a query.",
        "  • Keep `notes` to one short sentence — what was wrong with the parent's approach, in terms the parent can apply next time.",
        "  • If the task genuinely can't be expressed as a single analytics_query (needs python post-processing, multiple queries joined, etc.), return a spec for the closest single query that gets the data the python step would need, and call that out in `notes`.",
        "",
        currentDateSection(),
    ].join("\n");
}

export function createSpawnQueryTool({ toolRegistry, getThinkingLevel, serverChatId = null } = {}) {
    return {
        name: "spawn_query",
        label: "Spawn query specialist",
        description:
            "Hand a tricky analytics_query off to a focused sub-agent that knows the schema deeply. Returns a VALIDATED Query spec (and an optional note about what was wrong) — YOU then call `analytics_query` with that spec yourself, so the rows land in your own context the normal way.\n\n" +
            "When to use:\n" +
            "  • analytics_query gave you a schema error (unknown field, wrong operator, etc.) and you're not sure how to fix it.\n" +
            "  • You're about to retry the same broken spec for the third time.\n" +
            "  • You want to compose a complex multi-traversal query and don't have all the field names in your head.\n\n" +
            "The specialist has schema_list_entities / schema_describe_entity / schema_search / analytics_query (for dry-runs). It iterates until the spec runs cleanly, then returns it. This is cheaper than fighting the same query yourself across many turns AND cheaper than spawn_subagent with a vague task — the contract here is narrow and the result is a small structured spec.\n\n" +
            "Output: `{ spec: <Query spec>, notes?: <one-line explanation> }`. To use it: read the spec out of the result, then call `analytics_query` with that exact spec as your `args.spec`.",
        parameters: Type.Object({
            task: Type.String({
                description: "Describe what you want to query: which entity, what filters, what columns / aggregates you need, what the result should look like. Include any context from the user's prompt that would help the specialist understand intent.",
            }),
            failed_attempt: Type.Optional(
                Type.Unknown({
                    description: "Optional: the spec you tried that didn't work. Including it lets the specialist see what shape you were going for and what was off — faster than them rebuilding from scratch.",
                }),
            ),
            failed_error: Type.Optional(
                Type.String({
                    description: "Optional: the error message analytics_query returned for your failed_attempt. The specialist reads this directly to know what to fix.",
                }),
            ),
        }),
        async execute(_id, args, signal, onUpdate) {
            const taskLines = [
                "Task:",
                String(args?.task || "").trim() || "(no task provided)",
            ];
            if (args?.failed_attempt !== undefined) {
                let attemptJson;
                try { attemptJson = JSON.stringify(args.failed_attempt, null, 2); }
                catch { attemptJson = String(args.failed_attempt); }
                taskLines.push("", "Parent's failed attempt:", "```json", attemptJson, "```");
            }
            if (typeof args?.failed_error === "string" && args.failed_error.trim()) {
                taskLines.push("", "Error from that attempt:", args.failed_error.trim());
            }
            taskLines.push(
                "",
                "Produce a working spec, dry-run it via analytics_query, and call return_result with { spec, notes } when it passes.",
            );

            const allTools = (toolRegistry?.() || []).filter((t) => SPECIALIST_TOOLKIT.includes(t.name));
            const state = {
                kind: "subagent",
                taskSummary: summarise(args?.task),
                status: "running",
                activity: "starting…",
                tools: [],
                preview: "",
                result: null,
                summary: null,
                messages: [],
                task: taskLines.join("\n"),
                systemPrompt: buildSpecialistSystemPrompt(),
            };
            const emit = () => {
                try { onUpdate?.({ details: { ...state } }); } catch { /* noop */ }
            };
            emit();

            const onEvent = (event) => {
                forwardChildEvent(state, event);
                emit();
            };
            const onChildReady = (child) => {
                mirrorTranscript(state, child);
            };

            const { status, result, error } = await runSubAgent({
                task: state.task,
                systemPrompt: state.systemPrompt,
                tools: allTools,
                outputSchema: OUTPUT_SCHEMA,
                signal,
                onEvent,
                onChildReady,
                // The query specialist runs the parent's model — give it the
                // same thinking depth the user picked for the chat.
                thinkingLevel: getThinkingLevel?.(),
                // Attribute the specialist's LLM proxy calls to the chat.
                serverChatId,
            });

            const ok = status === "done" && result && typeof result === "object" && result.spec;
            state.status = ok ? "done" : status === "aborted" ? "stopped" : "error";
            state.activity = null;
            state.result = result;
            state.summary = ok
                ? (typeof result.notes === "string" && result.notes.trim()
                    ? `spec built · ${truncate(result.notes.trim(), 80)}`
                    : "spec built")
                : status === "aborted"
                    ? "stopped by user"
                    : `error · ${error?.message || "specialist failed to return a spec"}`;
            emit();

            if (!ok) {
                const message = state.summary;
                return {
                    content: [{ type: "text", text: message }],
                    details: { ...state },
                    isError: status !== "aborted",
                };
            }

            // Parent reads `spec` out of this JSON and uses it
            // directly in their next analytics_query call. The
                // `notes` line shows up in the parent's context so the
            // model picks up the lesson naturally.
            const payload = {
                spec: result.spec,
                notes: typeof result.notes === "string" ? result.notes : null,
            };
            return {
                content: [{ type: "text", text: JSON.stringify(payload) }],
                details: { ...state },
            };
        },
    };
}

// Lightweight progress mirror so the chat UI's SubAgentSingle
// renderer (which keys off details.kind === "subagent") shows
// running tool calls + preview just like spawn_subagent. Kept
// inline to avoid pulling subAgentTools.js's forwardChildEvent —
// they're the same shape but spawn_query doesn't need the throttle
// machinery for batch-style fan-out.
const PREVIEW_TAIL = 200;
function forwardChildEvent(slot, event) {
    refreshMirror(slot);
    switch (event.type) {
        case "turn_start":
            slot.activity = "thinking…";
            slot.preview = "";
            return;
        case "tool_execution_start": {
            const name = event.toolName || "tool";
            slot.tools = [...(slot.tools || []), { name, status: "running", summary: "" }];
            slot.activity = `running ${name}…`;
            return;
        }
        case "tool_execution_end": {
            const tools = (slot.tools || []).slice();
            const last = tools.length ? tools[tools.length - 1] : null;
            if (last) {
                tools[tools.length - 1] = {
                    ...last,
                    status: event.isError ? "error" : "done",
                    summary: event.isError ? "error" : "done",
                };
            }
            slot.tools = tools.length > 6 ? tools.slice(-6) : tools;
            slot.activity = event.isError ? "tool failed" : "thinking…";
            return;
        }
        case "message_update": {
            const inner = event.assistantMessageEvent;
            if (!inner) return;
            if (inner.type === "text_delta" && inner.delta) {
                slot.preview = ((slot.preview || "") + inner.delta).slice(-PREVIEW_TAIL);
                slot.activity = "writing…";
            } else if (inner.type === "thinking_delta" && inner.delta) {
                slot.preview = ((slot.preview || "") + inner.delta).slice(-PREVIEW_TAIL);
                slot.activity = "reasoning…";
            } else if (inner.type === "toolcall_start") {
                slot.activity = "preparing tool call…";
            }
            return;
        }
        default:
            return;
    }
}

function summarise(text) {
    if (!text) return "";
    const clean = String(text).replace(/\s+/g, " ").trim();
    return clean.length > 80 ? `${clean.slice(0, 77)}…` : clean;
}

function truncate(text, max) {
    if (text == null) return "";
    const s = String(text);
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
